'use client';

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import LoginModal from "./LoginModal";
import { useAuth } from "@/app/context/AuthContext";
import { getActiveCart } from "@/app/apis/cart";

/* Home + Services 下前五项，与 Sidebar 一致 */
const topNavItems = [
  { label: 'HOME', href: '/', icon: 'home' },
  { label: 'CNC', href: '/services/cnc-machining', icon: 'cnc' },
  { label: 'SHEET METAL', href: '/services/sheet-metal-fabrication', icon: 'sheet' },
  { label: 'INJECT MOLDING', href: '/services/injection-molding', icon: 'inject-molding' },
  { label: '3D PRINTING', href: '/services/3d-printing', icon: '3d' },
  { label: 'PCB/PCBA', href: '/services/pcb-pcba', icon: 'pcb' },
];

function NavIcon({ type }: { type: string }) {
  const size = 28;
  // 其余图标从 public 中加载对应 SVG：
  // 常规：/cnc.svg、/sheet.svg ...
  // 选中/悬浮：/cnc-select.svg、/sheet-select.svg ...
    return (
      <>
        <img
          src={`/${type}.svg`}
          alt={type}
          width={size}
          height={size}
          className="nav-icon nav-icon--default"
          style={{ objectFit: 'contain' }}
        />
        <img
          src={`/${type}-select.svg`}
          alt={`${type}-select`}
          width={size}
          height={size}
          className="nav-icon nav-icon--active"
          style={{ objectFit: 'contain' }}
        />
      </> 
    );
}

export default function TopBar() {
  const pathname = usePathname();
  const router = useRouter();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const { isAuthenticated, logout } = useAuth();
  
  // 从全局认证上下文获取登录状态
  const isLoggedIn = isAuthenticated;

  const handleUserClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      setLoginModalOpen(true);
      setUserDropdownOpen(false);
    }
    // 已登录状态下使用悬浮显示，不需要点击切换
  };

  const handleUserMouseEnter = () => {
    if (isLoggedIn) {
      setUserDropdownOpen(true);
    }
  };

  const handleUserMouseLeave = () => {
    if (isLoggedIn) {
      setUserDropdownOpen(false);
    }
  };

  const handleAccountLinkClick = (e: React.MouseEvent) => {
    if (!isLoggedIn) {
      e.preventDefault();
      setLoginModalOpen(true);
      setUserDropdownOpen(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadCartCount() {
      if (!isLoggedIn) {
        setCartCount(0);
        return;
      }
      try {
        const resp = await getActiveCart();
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        if (!cancelled) setCartCount(rows.length);
      } catch {
        if (!cancelled) setCartCount(0);
      }
    }

    void loadCartCount();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, pathname]);

  return (
    <header className="topbar-header sticky top-0 h-20 bg-white border-b z-30 w-full">
      <div className="topbar-inner">
      {/* 左侧横向导航：图标在上、文字在下，等宽网格；每项 Link 内再包一层结构 */}
      <nav className="topbar-nav">
        {topNavItems.map((item) => {
          const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
          return (
            <Link
              key={item.href + item.label}
              href={item.href}
              className={`topbar-nav__item ${isActive ? 'topbar-nav__item--active' : ''}`}
            >
              <div className="topbar-nav__tile">
                <div className="topbar-nav__tile-inner">
                  <span className="topbar-nav__icon">
                    <NavIcon type={item.icon} />
                  </span>
                  <span className="topbar-nav__label">{item.label}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* 右侧：Contact Us + 用户/购物车（与顶栏同高、内容垂直居中） */}
      <div className="topbar-right flex items-center gap-4 shrink-0 self-stretch">
      
        <div className="header__icon-list">
        <ul  style={{ padding: '0 20px',display: 'flex', alignItems: 'center', gap:'20px'}}>
          {/* User Icon and Dropdown Menu */}
          <li 
            id="usercontainer"
            onMouseEnter={handleUserMouseEnter}
            onMouseLeave={handleUserMouseLeave}
          >
            <a
              href="#"
              id="btlogin"
              aria-label="Login"
              className="userlink"
              onClick={handleUserClick}
            >
              {isLoggedIn ? (
               <img src="/user.svg" alt="" width={32} height={32} />
              ) : (
                <span className="topbar-signin-text">Sign In</span>
              )}
            </a>
            {userDropdownOpen && isLoggedIn && (
              <div 
                className="userdropdown" 
                style={{ display: 'block' }}
                onMouseEnter={handleUserMouseEnter}
                onMouseLeave={handleUserMouseLeave}
              >
                <Link href="/account/myaccount" onClick={handleAccountLinkClick}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="20" height="20" rx="5" stroke="black" strokeWidth="2"></rect>
                    <circle cx="12" cy="8" r="3" stroke="black" strokeWidth="2"></circle>
                    <path d="M6 20C6 16.6863 8.68629 14 12 14C15.3137 14 18 16.6863 18 20" stroke="black" strokeWidth="2"></path>
                  </svg>
                  <span id="useraccount">Account Info</span>
                </Link>
                <Link href="/account/orders" onClick={handleAccountLinkClick}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="3" width="16" height="18" rx="2" stroke="black" strokeWidth="2"></rect>
                    <path d="M8 7H16" stroke="black" strokeWidth="2"></path>
                    <path d="M8 11H16" stroke="black" strokeWidth="2"></path>
                    <path d="M8 15H12" stroke="black" strokeWidth="2"></path>
                  </svg>
                  Project Hub
                </Link>
                <Link href="/account/invoices" onClick={handleAccountLinkClick}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="3" width="16" height="18" rx="2" stroke="black" strokeWidth="2"></rect>
                    <path d="M8 7H16" stroke="black" strokeWidth="2"></path>
                    <path d="M8 11H16" stroke="black" strokeWidth="2"></path>
                    <path d="M8 15H12" stroke="black" strokeWidth="2"></path>
                  </svg>
                  My Invoices
                </Link>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    logout();
                    setUserDropdownOpen(false);
                    router.push('/');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 16L14 12L10 8" stroke="black" strokeWidth="2"></path>
                    <path d="M14 12H3" stroke="black" strokeWidth="2"></path>
                    <path d="M16 4H19C20.1046 4 21 4.89543 21 6V18C21 19.1046 20.1046 20 19 20H16" stroke="black" strokeWidth="2"></path>
                  </svg>
                  Logout
                </a>
              </div>
            )}
          </li>
          
          {/* Shopping Cart Icon */}
          <li>
            <Link
              href="/account/mycart"
              className="userlink"
              onClick={handleAccountLinkClick}
            >
             <img src="/cart.svg" alt="" width={32} height={32} />
              <span className="header__cart-count header__cart-count--floating bubble-count">{cartCount}</span>
            </Link>
          
          </li>
        </ul>
        </div>

          <Link
          href="/company/contact-us"
          className="header__contact-manager contact-us-btn topbar-contact-btn"
        >
          Contact Us
        </Link>
      </div>
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </header>
  );
}
